/**
 * Record ids are identifiers, and identifiers in a URL are personal data once
 * they are sitting in someone else's database. Every id in a portal path is
 * replaced by a placeholder before it is sent anywhere, so
 * /portal/v3/operator/MTI6MzQ1/compliance?itemId=OTk6MQ becomes
 * /portal/v3/operator/:id/compliance?itemId=:id and the shape of the page
 * survives while the record does not.
 *
 * Both telemetry sinks use this - Grafana Faro in plugins/rum.js and PostHog in
 * plugins/analytics.js - so a URL cannot be scrubbed for one and leak through
 * the other. Anything added here is covered everywhere at once.
 */
const SCRUB_RULES = [
  // Credentials, first, before anything else looks at the URL.
  //
  // The welcome email links to /?email=...&password=...&tab=company, so a new
  // customer's address and password ride in the query string of the first page
  // they ever open. Six events carrying a real password reached PostHog on
  // 2026-09-21 before this rule existed. The name is kept so the shape of the
  // link is still readable; only the value goes.
  //
  // This is a backstop, not the fix. That URL is also in the customer's browser
  // history, in any referrer it sends, and in the gateway's own access log -
  // none of which this file can reach. The link itself is what needs changing.
  { find: /([?&](password|pwd|pass|email|token|code|secret|apiKey|api_key|auth|signature)=)[^&#]*/gi, replace: '$1:redacted' },

  // An id encoded by ContentUtils.baseEncode carries an RC- prefix, wherever it
  // sits in the path. Matching the id itself rather than its position is what
  // catches /admin/accounts/RC-.../map, where the id is the second segment and
  // a positional rule scrubs the word "accounts" instead. The slash is left out
  // of the id's own characters so the rest of the path is not eaten with it -
  // base64 of a #n:m record id cannot contain one.
  { find: /RC-[A-Za-z0-9+=_-]+/g, replace: ':id' },

  // A bare id - baseEncode without the prefix - still has to go. It is told
  // apart from a route word by its shape: an encoded id always carries a
  // capital, a digit or padding, and every literal segment in router.js
  // (accounts, companies, billing, gps, llm) is lower-case letters only. The
  // old rule replaced whatever followed /admin/, which turned the name of an
  // admin page into :id and lost which page a measurement came from.
  { find: /\/(operator|admin|customer)\/(?=[^/?#]*[A-Z0-9=])[^/?#]+/g, replace: '/$1/:id' },

  { find: /([?&](itemId|employeeId|userId|vehicleId|inspectionId|timesheetId|reportId|companyId)=)[^&#]*/gi, replace: '$1:id' },
  { find: /#\d+:\d+/g, replace: '#:rid' },
];

export const scrubUrl = (url) => {
  if (!url) {
    return url;
  }
  let scrubbed = String(url);
  SCRUB_RULES.forEach((rule) => {
    scrubbed = scrubbed.replace(rule.find, rule.replace);
  });
  return scrubbed;
};

export default scrubUrl;
